import React, { useState, useEffect, useRef } from 'react';
import API from '../utils/api';

const EMPTY_SVC = { name: '', slug: '', description: '', icon: '✨', category: 'general', pill: '', order: 0 };
const EMPTY_SUB = { name: '', description: '', price: '', duration: '', order: 0 };

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_SVC);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [subForm, setSubForm] = useState(EMPTY_SUB);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    API.get('/services').then(r => { if (r.data.success) setServices(r.data.data); }).catch(() => { }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (fileRef.current?.files[0]) fd.append('image', fileRef.current.files[0]);
    try {
      if (editing) {
        const r = await API.patch(`/services/${editing}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (r.data.success) setServices(prev => prev.map(s => s._id === editing ? r.data.data : s));
      } else {
        const r = await API.post('/services', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (r.data.success) setServices(prev => [r.data.data, ...prev]);
      }
      setForm(EMPTY_SVC); setEditing(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      alert(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const startEdit = (svc) => {
    setForm({ name: svc.name, slug: svc.slug, description: svc.description || '', icon: svc.icon || '✨', category: svc.category || 'general', pill: svc.pill || '', order: svc.order || 0 });
    setEditing(svc._id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const del = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    await API.delete(`/services/${id}`);
    setServices(prev => prev.filter(s => s._id !== id));
  };

  const addSub = async (svcId) => {
    if (!subForm.name.trim()) return;
    const r = await API.post(`/services/${svcId}/subservices`, subForm);
    if (r.data.success) { setServices(prev => prev.map(s => s._id === svcId ? r.data.data : s)); setSubForm(EMPTY_SUB); }
  };

  const removeSub = async (svcId, subId) => {
    const r = await API.delete(`/services/${svcId}/subservices/${subId}`);
    if (r.data.success) setServices(prev => prev.map(s => s._id === svcId ? r.data.data : s));
  };

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h2 className="admin-page__title">{editing ? 'Edit Service' : 'Services'}</h2>
      </div>

      {/* Form */}
      <div className="admin-card">
        <h3 className="admin-card__title">{editing ? '✏️ Editing Service' : '+ New Service'}</h3>
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="admin-form__row">
            <div className="admin-fg">
              <label className="admin-fl">Service Name *</label>
              <input type="text" className="admin-fi" value={form.name} required
                onChange={e => setForm(f => ({ ...f, name: e.target.value, slug: toSlug(e.target.value) }))} placeholder="e.g. Bridal Makeup" />
            </div>
            <div className="admin-fg">
              <label className="admin-fl">Slug (auto)</label>
              <input type="text" className="admin-fi" value={form.slug}
                onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} placeholder="bridal-makeup" />
            </div>
          </div>
          <div className="admin-fg">
            <label className="admin-fl">Description</label>
            <textarea className="admin-fi admin-fi--ta" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Short description…" />
          </div>
          <div className="admin-form__row">
            <div className="admin-fg">
              <label className="admin-fl">Icon (emoji)</label>
              <input type="text" className="admin-fi" value={form.icon}
                onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} placeholder="✨" />
            </div>
            <div className="admin-fg">
              <label className="admin-fl">Category</label>
              <input type="text" className="admin-fi" value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="bridal / hair / skin…" />
            </div>
            <div className="admin-fg">
              <label className="admin-fl">Pill Label</label>
              <input type="text" className="admin-fi" value={form.pill}
                onChange={e => setForm(f => ({ ...f, pill: e.target.value }))} placeholder="Bridal / Glam…" />
            </div>
            <div className="admin-fg">
              <label className="admin-fl">Order</label>
              <input type="number" className="admin-fi" value={form.order}
                onChange={e => setForm(f => ({ ...f, order: e.target.value }))} />
            </div>
          </div>
          <div className="admin-fg">
            <label className="admin-fl">Service Image</label>
            <input type="file" ref={fileRef} accept="image/*" className="admin-fi" />
          </div>
          <div className="admin-form__btns">
            <button type="submit" className="admin-btn admin-btn--primary" disabled={saving}>{saving ? 'Saving…' : editing ? 'Update Service' : 'Add Service'}</button>
            {editing && <button type="button" className="admin-btn admin-btn--outline" onClick={() => { setEditing(null); setForm(EMPTY_SVC); }}>Cancel</button>}
          </div>
        </form>
      </div>

      {/* Services list */}
      {loading ? <div className="admin-loading">Loading…</div> : (
        <div className="admin-services-list">
          {services.map(svc => (
            <div key={svc._id} className="admin-svc-item">
              <div className="admin-svc-item__header">
                {svc.imageUrl && <img src={svc.imageUrl} alt={svc.name} className="admin-svc-item__img" />}
                <div className="admin-svc-item__info">
                  <span className="admin-svc-item__icon">{svc.icon}</span>
                  <div>
                    <div className="admin-svc-item__name">{svc.name}</div>
                    <div className="admin-svc-item__slug">{svc.slug}</div>
                    <div className="admin-svc-item__desc">{svc.description}</div>
                  </div>
                </div>
                <div className="admin-svc-item__actions">
                  <span className="admin-badge admin-badge--pink">{svc.subServices?.length || 0} sub</span>
                  <button className="admin-btn admin-btn--outline admin-btn--sm" onClick={() => setExpanded(expanded === svc._id ? null : svc._id)}>
                    {expanded === svc._id ? '▲ Sub-services' : '▼ Sub-services'}
                  </button>
                  <button className="admin-btn admin-btn--outline admin-btn--sm" onClick={() => startEdit(svc)}>Edit</button>
                  <button className="admin-btn admin-btn--danger admin-btn--sm" onClick={() => del(svc._id)}>Delete</button>
                </div>
              </div>

              {/* Sub-services panel */}
              {expanded === svc._id && (
                <div className="admin-svc-item__subs">
                  <div className="admin-sub-list">
                    {svc.subServices?.map(sub => (
                      <div key={sub._id} className="admin-sub-item">
                        <div className="admin-sub-item__info">
                          <span className="admin-sub-item__name">{sub.name}</span>
                          {sub.description && <span className="admin-sub-item__desc">{sub.description}</span>}
                          <div className="admin-sub-item__meta">
                            {sub.duration && <span>⏱ {sub.duration}</span>}
                            {sub.price && <span>₹ {sub.price}</span>}
                          </div>
                        </div>
                        <button className="admin-btn admin-btn--danger admin-btn--xs" onClick={() => removeSub(svc._id, sub._id)}>✕</button>
                      </div>
                    ))}
                    {(!svc.subServices || svc.subServices.length === 0) && <p className="admin-empty admin-empty--sm">No sub-services yet.</p>}
                  </div>
                  {/* Add sub-service inline */}
                  <div className="admin-sub-form">
                    <h4 className="admin-sub-form__title">+ Add Sub-service</h4>
                    <div className="admin-form__row">
                      <input type="text" className="admin-fi" value={subForm.name} onChange={e => setSubForm(f => ({ ...f, name: e.target.value }))} placeholder="Name *" />
                      <input type="text" className="admin-fi" value={subForm.description} onChange={e => setSubForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" />
                      <input type="text" className="admin-fi" value={subForm.duration} onChange={e => setSubForm(f => ({ ...f, duration: e.target.value }))} placeholder="Duration (e.g. 45 min)" />
                      <input type="text" className="admin-fi" value={subForm.price} onChange={e => setSubForm(f => ({ ...f, price: e.target.value }))} placeholder="Price (e.g. ₹499+)" />
                      <button className="admin-btn admin-btn--primary" onClick={() => addSub(svc._id)}>Add</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
